import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type MeResponse } from './api';

const SESSION_KEY = ['session'] as const;

/**
 * Source de vérité de la session côté front. Le cookie étant httpOnly, on ne
 * peut pas le lire : on demande au serveur qui nous sommes. Un 401 signifie
 * simplement « pas connecté » et ne doit pas être traité comme une panne.
 */
export function useSession() {
  const query = useQuery<MeResponse | null>({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      try {
        return await api<MeResponse>('/api/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    session: query.data ?? null,
    isLoading: query.isPending,
    isError: query.isError,
  };
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (creds: { username: string; password: string }) =>
      api<{ user: MeResponse['user'] }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(creds),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSION_KEY }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      // On vide tout : aucun reliquat de données d'un rep ne doit survivre
      // à la déconnexion sur un iPad partagé.
      qc.clear();
    },
  });
}
